import { useMutation } from "@tanstack/react-query";
import { useTRPC, type RouterInputs } from "@/trpc";
import type { CredentialVaultScope } from "../lib/credential-vault-scope";

type CreateCredentialInput = Omit<
  RouterInputs["credentials"]["createMyCredential"],
  "switchUserId"
>;
type UpdateCredentialInput = Omit<
  RouterInputs["credentials"]["updateMyCredential"],
  "switchUserId"
>;

/** The shared form owns one atomic draft; only its record owner selects transport. */
export function useCredentialVaultRecordMutations(scope: CredentialVaultScope) {
  const trpc = useTRPC();
  const providerCreate = useMutation(
    trpc.credentials.createMyCredential.mutationOptions(),
  );
  const providerUpdate = useMutation(
    trpc.credentials.updateMyCredential.mutationOptions(),
  );
  const providerRemove = useMutation(
    trpc.credentials.removeMyCredential.mutationOptions(),
  );
  const orgCreate = useMutation(trpc.credentials.createForBu.mutationOptions());
  const orgUpdate = useMutation(trpc.credentials.updateForBu.mutationOptions());
  const orgRemove = useMutation(trpc.credentials.removeForBu.mutationOptions());

  function create(input: CreateCredentialInput) {
    if (scope.kind === "organization") {
      return orgCreate.mutateAsync({
        ...input,
        organizationBusinessUnitId: scope.organizationBusinessUnitId,
        providerUserId: scope.providerUserId,
        ...(scope.expectedAffiliationId
          ? { expectedAffiliationId: scope.expectedAffiliationId }
          : {}),
      });
    }
    return providerCreate.mutateAsync({ ...input, switchUserId: scope.userId });
  }
  function update(input: UpdateCredentialInput) {
    if (scope.kind === "organization") {
      return orgUpdate.mutateAsync({
        ...input,
        organizationBusinessUnitId: scope.organizationBusinessUnitId,
        providerUserId: scope.providerUserId,
        ...(scope.expectedAffiliationId
          ? { expectedAffiliationId: scope.expectedAffiliationId }
          : {}),
      });
    }
    return providerUpdate.mutateAsync({ ...input, switchUserId: scope.userId });
  }
  function remove(credentialId: string) {
    if (scope.kind === "organization") {
      return orgRemove.mutateAsync({
        credentialId,
        organizationBusinessUnitId: scope.organizationBusinessUnitId,
        providerUserId: scope.providerUserId,
        ...(scope.expectedAffiliationId
          ? { expectedAffiliationId: scope.expectedAffiliationId }
          : {}),
      });
    }
    return providerRemove.mutateAsync({
      credentialId,
      switchUserId: scope.userId,
    });
  }
  return {
    create,
    update,
    remove,
    isPending:
      providerCreate.isPending ||
      providerUpdate.isPending ||
      providerRemove.isPending ||
      orgCreate.isPending ||
      orgUpdate.isPending ||
      orgRemove.isPending,
  };
}
